import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, Calendar, Clock, Share2, Phone, Eye } from 'lucide-react';
import { articles, seasonalGuides, getArticleSeason } from './BlogPage';
import { generateArticleSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';
import { SeasonalOfferBanner } from '../components/specials/SeasonalOfferBanner';
import { fetchArticleViews, recordArticleView, formatReads, TRENDING_THRESHOLD } from '../utils/blogViews';
import { linkifyPhoneHtml } from '../components/PhoneLinkText';

const BlogArticlePage = () => {
  const { slug } = useParams();
  const article = articles.find(a => a.slug === slug);
  const [viewCount, setViewCount] = React.useState(null);

  useEffect(() => {
    if (!article) return undefined;
    setViewCount(null);
    fetchArticleViews(article.slug).then(setViewCount).catch(() => {});
    // Count a read only after 3s on page
    const timer = setTimeout(() => {
      recordArticleView(article.slug).then(v => v !== null && setViewCount(v)).catch(() => {});
    }, 3000);
    return () => clearTimeout(timer);
  }, [article]);

  // Generate schemas for valid articles
  const articleSchema = article ? generateArticleSchema(article) : null;
  const breadcrumbSchema = article ? generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Blog', url: 'https://comfortmasterheating.com/blog' },
    { name: article.title, url: `https://comfortmasterheating.com/blog/${article.slug}` }
  ]) : null;

  // Inject schemas into document head
  useSchemaMarkup(
    article ? [articleSchema, breadcrumbSchema] : [],
    `blog-${slug}`
  );

  if (!article) {
    return (
      <main className="py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-heading font-bold text-4xl text-slate-900 mb-4">Article Not Found</h1>
          <p className="text-slate-600 mb-8">The article you are looking for does not exist.</p>
          <Link to="/blog" className="btn-primary">Back to Blog</Link>
        </div>
      </main>
    );
  }

  // Determine seasonal guide type
  const seasonType = getArticleSeason(article);
  const seasonCfg = seasonalGuides[seasonType];
  const isSeasonalGuide = Boolean(seasonCfg);
  const SeasonIcon = seasonCfg?.icon;
  const seasonGradient = seasonCfg?.colors.gradient;
  const seasonBgColor = seasonCfg?.colors.solid;
  const seasonTitle = seasonCfg?.title;

  // Related: same seasonal guide, otherwise same category
  const relatedArticles = articles
    .filter(a => a.id !== article.id && (isSeasonalGuide ? getArticleSeason(a) === seasonType : a.category === article.category))
    .slice(0, 3);

  // Use content if available, otherwise use excerpt
  const articleContent = article.content || article.excerpt;

  return (
    <main>
      <Helmet>
        <title>{article.title} | ComfortMaster Blog</title>
        <meta name="description" content={article.excerpt} />
        <link rel="canonical" href={`https://comfortmasterheating.com/blog/${article.slug}`} />
      </Helmet>

      {/* Hero */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-4" data-testid="back-to-blog">
            <ArrowLeft size={18} />
            Back to Blog
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {isSeasonalGuide && (
              <span className={`inline-flex items-center gap-1 bg-gradient-to-r ${seasonGradient} text-white px-3 py-1 rounded-full text-sm font-bold`}>
                <SeasonIcon size={14} />
                {seasonTitle}
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSeasonalGuide ? `${seasonBgColor} text-white` : 'bg-emerald-600 text-white'}`}>{article.category}</span>
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl mb-6">{article.title}</h1>
          <div className="flex items-center gap-6 text-stone-400">
            <span className="flex items-center gap-2"><Calendar size={16} /> {article.date}</span>
            {viewCount >= TRENDING_THRESHOLD && (
              <span className="flex items-center gap-2" data-testid="article-read-count"><Eye size={16} /> {formatReads(viewCount)}</span>
            )}
            <span className="flex items-center gap-2"><Clock size={16} /> {article.readTime}</span>
          </div>
        </div>
      </section>

      {isSeasonalGuide && <SeasonalOfferBanner season={seasonType} seasonCfg={seasonCfg} />}

      {/* Article Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <img 
            src={article.image} 
            alt={article.title}
            className="w-full h-80 object-cover rounded-2xl mb-10"
          />
          <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:font-bold prose-h2:text-2xl prose-h2:text-slate-900 prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:text-slate-800 prose-h3:mt-6 prose-h3:mb-3 prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4 prose-ul:text-slate-700 prose-ul:my-4 prose-li:mb-2 prose-strong:text-slate-900">
            {article.content ? (
              <div dangerouslySetInnerHTML={{ __html: linkifyPhoneHtml(article.content) }} />
            ) : (
              <p className="text-slate-700 text-lg leading-relaxed mb-8">
                {articleContent}
              </p>
            )}
            
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-lg my-8">
              <p className="text-emerald-800 font-medium">
                For expert advice specific to your situation, contact ComfortMaster Heating and Air. Our certified technicians serve Lexington, Nicholasville, and all of Central Kentucky.
              </p>
            </div>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Need Professional HVAC Help?</h2>
            <p className="text-slate-700 mb-6">
              At ComfortMaster, we have been serving Kentucky homeowners for over 25 years. Whether you need AC repair, furnace maintenance, or a complete system replacement, our team is ready to help.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                Schedule Service
              </Link>
              <a href="tel:8593576803" className="btn-secondary inline-flex items-center justify-center gap-2">
                <Phone size={18} />
                (859) 357-6803
              </a>
            </div>
          </div>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-stone-200">
            <div className="flex items-center justify-between">
              <Link to="/blog" className="inline-flex items-center gap-2 text-emerald-600 font-semibold" data-testid="back-link">
                <ArrowLeft size={18} />
                More Articles
              </Link>
              <button className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600" data-testid="share-btn">
                <Share2 size={18} />
                Share Article
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles - Seasonal Guide Specific */}
      {relatedArticles.length > 0 && (
        <section className={`py-16 ${isSeasonalGuide ? `bg-gradient-to-br ${seasonCfg.colors.light}` : 'bg-stone-100'}`} data-testid="related-articles-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              {isSeasonalGuide && (
                <span className={`inline-flex items-center gap-1 bg-gradient-to-r ${seasonGradient} text-white px-3 py-1 rounded-full text-sm font-bold mb-3`}>
                  <SeasonIcon size={14} />
                  {seasonTitle}
                </span>
              )}
              <h2 className="font-heading font-bold text-2xl text-slate-900">
                {isSeasonalGuide ? `More from the ${seasonCfg.name} Guide` : 'Related Articles'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map(related => {
                const relatedCfg = seasonalGuides[getArticleSeason(related)];
                const RelatedIcon = relatedCfg?.icon;
                const relatedGradient = relatedCfg?.colors.gradient;
                
                return (
                  <Link key={related.id} to={`/blog/${related.slug}`} className="group" data-testid={`related-${related.id}`}>
                    <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all h-full flex flex-col relative">
                      {relatedCfg && (
                        <div className="absolute top-3 right-3 z-10">
                          <span className={`inline-flex items-center gap-1 bg-gradient-to-r ${relatedGradient} text-white px-2 py-1 rounded-full text-xs font-bold`}>
                            <RelatedIcon size={10} />
                          </span>
                        </div>
                      )}
                      <img src={related.image} alt={related.title} className="w-full h-40 object-cover" />
                      <div className="p-4 flex-grow flex flex-col">
                        <h3 className="font-semibold text-slate-900 line-clamp-2 mb-2 group-hover:text-emerald-600 transition-colors">{related.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3 flex-grow">{related.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">{related.readTime}</span>
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-medium">
                            Read <ArrowRight size={14} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog" className="btn-secondary inline-flex items-center gap-2">
                View All Articles <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default BlogArticlePage;
